import { AppBar, Box, Divider, IconButton, Menu, MenuItem, Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useQueryClient } from "@tanstack/react-query";
import { resetDemoData } from "apiClients/encounters";
import chatIcon from "assets/svg/light/chat-icon.svg";
import profileSettingIcon from "assets/svg/light/profile-setting-icon.svg";
import GenerateBreadcrumbs from "components/GenerateBreadcrumbs";
import { LazyImage } from "components/common/Image";
import ReportAnIssueModal from "components/common/Modal/ReportAnIssueModal";
import ReleaseInfoModal from "components/common/Modal/releaseInfoModal";
import { LogoIcon } from "components/common/SVG/LogoIcon";
import { Refresh } from "components/common/SVG/Refresh";
import { StylusIcon } from "components/common/SVG/StylusIcon";
import CustomTooltip from "components/common/Tooltips/Custom";
import Text from "components/common/Typography/Text";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutApi, postOidcLogout } from "redux-store/features/Auth/Login";
import { resetPatientRecommendations } from "redux-store/features/SmartRecommendations";
import { getLocationsFromUrl, getLocationsList, setActiveLocation, setActiveProvider } from "redux-store/features/Locations";
import { appBar, logoAdminName, mainNavbar, mainNavbarInnerBox } from "styles/AppContainer/NavbarStyle";
import Logo from "../../../assets/svg/light/Logo.svg";
import BackgroundProcessStatus from "../../../components/BackgroundProcessStatus";
import RecentActivityDropdown from "../../../components/RecentActivityDropdown";
import { FeatureFlagsContext } from "../../../contexts/FeatureFlagsContext";
import {
  annotationInterfaceFeature,
  isFeatureEnabled,
  oidcFeature,
  recentActivityFeature,
  resetEncounters,
} from "../../../utils/featureFlags";
import { clearCompletedRefreshes } from "../../../hooks/usePatientDataRefresh";

const navbarStyles = {
  root: {
    "& .navbar-select-dropdown": {
      "& .MuiGrid-root.MuiGrid-container": {
        width: "auto !important",
        marginRight: "10px",
      },
    },
  },
  navSearch: {
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      border: "1px solid #E1E1E1",
      borderRadius: "15px",
      height: "30px",
    },
  },
  navIcons: {
    color: "#1C4588",
  },
  logOut: {
    "& .MuiList-root.MuiList-padding.MuiMenu-list": {
      paddingTop: "1px",
      paddingBottom: "10px",
    },
    "& p.MuiTypography-root": {
      fontSize: "14px",
      fontWeight: "400",
    },
    "& .MuiMenuItem-root": {
      paddingLeft: "25px",
      paddingRight: "25px",
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  },
};

const DISCLAIMER_TEXT =
  "This tool supports clinical decision-making but does not replace your independent judgment or serve as the sole basis for diagnosis or treatment.";

interface NavbarProps {
  onMenuToggle?: () => void;
}

const Navbar = ({ onMenuToggle }: NavbarProps) => {
  const theme = useTheme();
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down("xxl"));
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { patientId } = useParams();
  const [displayName, setDisplayName] = useState("");
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [isReportIssueOpen, setIsReportIssueOpen] = useState(false);
  const [openReleaseModal, setOpenReleaseModal] = useState(false);
  const fetchedUrlsRef = useRef(new Set());
  const { locations, activeLocation, activeProvider, next, isLocationsLoading } = useSelector((state: any) => state.locations);
  const { isRecording } = useSelector((state: any) => state.globalStates);
  const { firstName, title, lastName, username, photo, loggedIn, designation, userId, groups } = useSelector((state: any) => state.auth);

  // Determine which logout action to invoke based on OIDC feature flag
  const flagsData = useContext(FeatureFlagsContext);
  const oidcEnabled = isFeatureEnabled(flagsData, oidcFeature);
  const resetEncountersEnabled = isFeatureEnabled(flagsData, resetEncounters);
  const annotationEnabled = isFeatureEnabled(flagsData, annotationInterfaceFeature);
  const recentActivityEnabled = isFeatureEnabled(flagsData, recentActivityFeature);

  const handleLogoutAndClose = () => {
    if (isRecording) {
      const confirmLogout = window.confirm("You have ongoing recording. Are you sure you want to leave?");
      if (confirmLogout) {
        clearCompletedRefreshes(); // Clear recent activity data on logout
        oidcEnabled ? dispatch(postOidcLogout()) : dispatch(logoutApi());
        handleCloseUserMenu();
      }
    } else {
      clearCompletedRefreshes(); // Clear recent activity data on logout
      oidcEnabled ? dispatch(postOidcLogout()) : dispatch(logoutApi());
      handleCloseUserMenu();
    }
  };

  useEffect(() => {
    let displayName = username;
    if (firstName) {
      displayName = `${title ?? ""} ${firstName} ${lastName ?? ""}`;
    }

    setDisplayName(displayName);
  }, [firstName, lastName, username]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const settings = [
    ...(annotationEnabled
      ? [
          {
            title: "Annotations",
            iconComponent: StylusIcon,
            onClick: () => navigate("/annotations"),
          },
        ]
      : []),
    {
      title: "Account Settings",
      icon: profileSettingIcon,
      onClick: () => navigate("/settings?tab=profileSettings"),
    },
    {
      title: "Report An Issue",
      icon: chatIcon,
      onClick: () => setIsReportIssueOpen(true),
    },
  ];

  useEffect(() => {
    if (loggedIn) {
      dispatch(getLocationsList());
      fetchedUrlsRef.current = new Set();
    }
  }, [dispatch, loggedIn]);

  useEffect(() => {
    if (next && !isLocationsLoading && loggedIn && !fetchedUrlsRef.current.has(next)) {
      fetchedUrlsRef.current.add(next);
      dispatch(getLocationsFromUrl(`/api${next.split("/api")[1]}`));
    }
  }, [next, isLocationsLoading, dispatch, loggedIn]);

  // Apply role-based defaults on every login and page reload.
  // Only fires when values are unset (empty) so mid-session manual changes are not overridden.
  useEffect(() => {
    if (!loggedIn || !userId || !locations?.length) return;

    const isProvider = groups?.includes("Provider");

    if (!activeProvider?.value && !activeProvider?.isCleared) {
      if (isProvider) {
        dispatch(
          setActiveProvider({
            isUsersList: true,
            value: userId,
            label: `${title ?? ""} ${firstName ?? ""} ${lastName ?? ""}`,
          })
        );
      } else {
        dispatch(setActiveProvider({ value: "All Patients", label: "All Patients" }));
      }
    }

    if (!activeLocation?.value) {
      dispatch(setActiveLocation((isProvider ? { label: "All Locations", value: "all" } : locations[0]) as any));
    }
  }, [loggedIn, userId, locations, groups, activeProvider, activeLocation, dispatch, title, firstName, lastName]);

  const getInitials = (firstName: string, lastName: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return ""; // Return an empty string if both are missing
  };

  const showResetEncountersButton = resetEncountersEnabled && patientId !== undefined;

  const handleResetEncounters = async () => {
    try {
      await resetDemoData(patientId);
      dispatch(resetPatientRecommendations({ patientId }));
      queryClient.removeQueries({ queryKey: ["smartRecommendations", patientId] });
      toast.success(`Patient encounters reset successfully.`);
      navigate("/appointments");
    } catch {
      toast.error("Failed to reset patient encounters.");
    }
  };

  return (
    <>
      <AppBar sx={appBar} className="navbar">
        <Box sx={{ ...mainNavbar(), ...navbarStyles.root }}>
          <Toolbar sx={{ paddingLeft: "16px !important", minHeight: { xs: "48px !important", lg: "70px !important" } }}>
            <Box sx={mainNavbarInnerBox}>
              <Box sx={{ ...logoAdminName(theme), overflow: "hidden" }}>
                {/* Hamburger — visible only below lg, replaced by permanent sidebar at lg+ */}
                {isCompact && (
                  <IconButton onClick={onMenuToggle} size="small" sx={{ mr: "8px", flexShrink: 0, color: "text.primary" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <Box sx={{ width: "18px", height: "2px", bgcolor: "text.secondary", borderRadius: "1px" }} />
                      <Box sx={{ width: "18px", height: "2px", bgcolor: "text.secondary", borderRadius: "1px" }} />
                      <Box sx={{ width: "18px", height: "2px", bgcolor: "text.secondary", borderRadius: "1px" }} />
                    </Box>
                  </IconButton>
                )}
                <img
                  data-testid="nephrolyist-logo"
                  src={Logo}
                  alt="logo"
                  style={{
                    marginRight: isCompact ? "12px" : "37.7369px",
                    width: isCompact ? "20px" : "28.2631px",
                    height: isCompact ? "24px" : "36px",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ overflow: "hidden", minWidth: 0 }}>
                  <GenerateBreadcrumbs id={"bread-crumbs-id"} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                {showResetEncountersButton && (
                  <IconButton onClick={handleResetEncounters}>
                    <Refresh fill={theme.palette.common.grey} />
                  </IconButton>
                )}
                <BackgroundProcessStatus />
                {recentActivityEnabled && <RecentActivityDropdown />}
                {patientId && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 4px 4px 0",
                        margin: "0 0 0 16px",
                        [theme.breakpoints.up("md")]: {
                          borderRadius: "12px",
                        },
                        [theme.breakpoints.up("xxl")]: {
                          borderRadius: "4px",
                        },
                        background: "#EEE4FF",
                        width: "fit-content",
                      }}>
                      <CustomTooltip tooltipText={DISCLAIMER_TEXT} clickToOpen placement="bottom-start">
                        <Box
                          sx={{
                            fontSize: "10px",
                            fontWeight: 500,
                            color: "#7A7A7A",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            [theme.breakpoints.up("xxl")]: {
                              maxWidth: "800px",
                              display: "block",
                            },
                          }}>
                          <Box
                            style={{
                              margin: "4px 5px 0px 8px",
                              display: "flex",
                            }}>
                            <LogoIcon width="13px" height="16px" sx={{ margin: "0 5px 0 0" }} />
                            <Box>{isTabletOrSmaller ? "Disclaimer" : DISCLAIMER_TEXT}</Box>
                          </Box>
                        </Box>
                      </CustomTooltip>
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    flexGrow: 0,
                    display: "flex",
                    alignItems: "center",
                  }}>
                  <Menu
                    sx={{ mt: "45px", ...navbarStyles.logOut }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={Boolean(anchorElUser)}
                    data-testid="menu-logout-id"
                    onClick={handleCloseUserMenu}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "20px",
                      }}>
                      <Typography data-testid={`navbar-display-name`} variant="h3" fontWeight={700} color={"grey"}>
                        {displayName}
                      </Typography>
                      {designation && (
                        <Typography variant="body2" fontWeight={400} color={"grey"} sx={{ fontSize: "12px", marginTop: "4px" }}>
                          {designation}
                        </Typography>
                      )}
                    </Box>
                    <Divider variant="middle" />
                    {settings.map(({ onClick, icon, iconComponent: IconComponent, title }, index) => (
                      <MenuItem key={index} onClick={() => onClick()}>
                        <Box
                          sx={{
                            display: "flex",
                          }}>
                          {icon && (
                            <LazyImage
                              src={icon}
                              alt=""
                              effect="opacity"
                              threshold={0}
                              sx={{ marginRight: "5px", width: "20px", height: "20px" }}
                            />
                          )}
                          {IconComponent && (
                            <Box sx={{ marginRight: "5px", display: "flex", alignItems: "center" }}>
                              <IconComponent />
                            </Box>
                          )}
                          <Typography
                            data-testid={`${title}-button-id`}
                            sx={{
                              fontWeight: 500,
                              textAlign: "center",
                            }}>
                            {title}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                    <Divider variant="middle" />
                    <MenuItem onClick={() => setOpenReleaseModal(true)}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontWeight: 500,
                        }}>
                        Release Info
                      </Typography>
                    </MenuItem>
                    <MenuItem data-testid={`Logout-button-id`} onClick={() => handleLogoutAndClose()}>
                      <span
                        style={{
                          color: "#d3302f",
                          fontSize: "14px",
                          fontWeight: "700",
                        }}>
                        Log Out
                      </span>
                    </MenuItem>
                  </Menu>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginLeft: "16px",
                      marginRight: "8px",
                    }}>
                    <PhoneOutlinedIcon sx={{ color: "#2d3561", fontSize: "28px" }} />
                    <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "9px",
                          fontWeight: 700,
                          color: "#7A7A7A",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          lineHeight: 1.2,
                        }}>
                        Support
                      </Typography>
                      <Typography
                        component="a"
                        href="tel:+18882819364"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#2d3561",
                          textDecoration: "none",
                          lineHeight: 1.3,
                          "&:hover": { textDecoration: "underline" },
                        }}>
                        (888) 281-9364
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, marginLeft: "24px" }}>
                    <Box
                      sx={{
                        borderRadius: "100px",
                        width: "30px",
                        height: "30px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}>
                      {photo !== null && (
                        <LazyImage
                          src={photo}
                          alt="Profile"
                          effect="opacity"
                          threshold={50}
                          wrapperProps={{ "data-testid": "patient-picture" }}
                          sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "100px",
                            backgroundSize: "contain",
                            objectFit: "cover",
                            objectPosition: "50% 0",
                          }}
                        />
                      )}
                      {photo === null && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "20px",
                            background: "linear-gradient(76.4deg, #9969FF 11.51%, #DF7177 92.06%)",
                          }}>
                          <Text variant="body2" color={"white"}>
                            {getInitials(firstName, lastName)}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Toolbar>
        </Box>
      </AppBar>
      <ReportAnIssueModal
        isOpen={isReportIssueOpen}
        onClose={() => setIsReportIssueOpen(false)}
        mainDescription="Leave a comment below to report an issue with the Nephrolytics application. Our support team will work to resolve the issue."
      />
      <ReleaseInfoModal isOpen={openReleaseModal} onClose={() => setOpenReleaseModal(false)} />
    </>
  );
};

export default Navbar;
